import { useCallback, useReducer } from 'react';
import { useAuth } from 'react-oidc-context';

import { config } from '../../../config/config';
import { useEventDispatcher } from '../../eventing/components/eventing-service-provider/eventing-service-provider';
import { EventName } from '../../eventing/interfaces/events.interface';
import { getUser } from '../../main/util/auth';
import type { DocumentSide } from '../reducers/user-verification.reducer';
import {
    userVerificationReducer,
    USER_VERIFICATION_INITIAL_STATE,
} from '../reducers/user-verification.reducer';

export interface DocumentVerificationRequest {
    file: File;
    documentType?: string | null;
    side: DocumentSide;
    documentNumber?: string;
    expiresOn?: string;
}

export function useDocumentUpload() {
    const auth = useAuth();
    const dispatchEvent = useEventDispatcher();

    const [state, dispatch] = useReducer(userVerificationReducer, USER_VERIFICATION_INITIAL_STATE);

    const uploadDocument = useCallback(
        ({ file, documentType, side, documentNumber, expiresOn }: DocumentVerificationRequest) => {
            const user = getUser();
            dispatchEvent({ name: EventName.UPLOAD_DOCUMENT_KYC_ATTEMPT });

            return new Promise<void>((resolve, reject) => {
                dispatch({
                    type: 'loading',
                    payload: { side: side },
                });

                const request = new XMLHttpRequest();
                const formData = new FormData();
                const userProfileId = auth.user?.profile?.user_profile_id as string;

                formData.append('file', file);
                if (documentType) {
                    formData.append('documentType', documentType);
                }

                if (side) {
                    formData.append('side', side);
                }

                if (documentNumber !== undefined) {
                    formData.append('documentNumber', documentNumber);
                }

                if (expiresOn !== undefined) {
                    formData.append('expiresOn', expiresOn);
                }

                request.open(
                    'POST',
                    config.DOCUMENT_UPLOAD_ENDPOINT.replace(/\{userProfileId\}/g, userProfileId),
                );

                request.upload.addEventListener('progress', (event) => {
                    console.warn('event.loaded', event.loaded);
                    console.warn('event.total', event.total);
                    dispatch({
                        type: 'progress',
                        payload: { side: side, progress: (event.loaded / event.total) * 100 },
                    });
                });

                request.addEventListener('load', () => {
                    if (request.status >= 200 && request.status <= 299) {
                        dispatchEvent({ name: EventName.UPLOAD_DOCUMENT_KYC_SUCCESS });
                        dispatch({
                            type: 'complete',
                            payload: { side: side },
                        });
                        resolve();
                    } else {
                        dispatch({
                            type: 'error',
                            payload: { side: side },
                        });
                        reject();
                    }
                });

                request.addEventListener('error', () => {
                    dispatchEvent({
                        name: EventName.UPLOAD_DOCUMENT_KYC_FAILED,
                    });
                    dispatch({
                        type: 'error',
                        payload: { side: side },
                    });

                    reject();
                });

                request.setRequestHeader('Authorization', `Bearer ${user?.access_token}`);
                request.setRequestHeader('X-Tenant-Id', config.TENANT_ID);
                request.send(formData);
            });
        },
        [auth.user?.profile?.user_profile_id, dispatchEvent],
    );

    return { uploadDocument, sides: state.sides };
}
